// FindNetSuiteVendorPopup.tsx
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { makeStyles } from 'tss-react/mui';
import { FormProvider, useForm } from 'react-hook-form';

import Loader from './Loader';
import FormSelectField from './formFields/FormSelectField';
import FormTextField from './formFields/FormTextField';
import CommonButton from './buttons/CommonButton';
import useSnackBar from '../hooks/useSnackBar';
import { SEARCH_BY_OPTIONS_NETSUITE, SEARCH_BY_OPTIONS_QUICKBOOKS } from '../constants/constants';
import {
  searchNetSuiteVendor,
  NetSuiteVendorSearchField,
  getNetSuiteVendor,
  QuickBookVendorSearchField,
} from '../api/paymentsApi/settlementsApi';
import NetSuiteVendorReviewPopup from './NetSuiteVendorReviewPopup';
import TruncatedText from './TruncatedText';
import { NetSuiteVendor } from '../views/VendorsView/types';
import { VendorStatusMap } from '../views/FirmBankingView/constants';
import { NATIVE_ENTITY_TYPE_MAP, accountingVendorType } from '../views/VendorsView/helpers';

const useStyles = makeStyles()((theme) => ({
  popupContainer: {
    '& .MuiDialog-paper': {
      width: '1100px',
      maxWidth: 'calc(100vw - 40px)',
      maxHeight: '80vh',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.common.white,
      boxSizing: 'border-box',
      borderRadius: '16px',
      padding: '32px',
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  title: {
    fontSize: '22px',
    fontWeight: 700,
    fontFamily: 'Inter',
  },
  subtitle: {
    marginTop: '10px',
    color: theme.palette.secondary.main,
    fontSize: '16px',
    fontFamily: 'Inter',
  },
  closeButton: {
    color: theme.palette.secondary.main,
    padding: 0,
  },
  searchRow: {
    display: 'grid',
    gridTemplateColumns: '240px 1fr',
    gap: '12px',
    marginTop: '20px',

    '@media (max-width: 650px)': {
      gridTemplateColumns: '1fr',
    },
  },
  tableContainer: {
    width: '100%',
    marginTop: '12px',
    maxHeight: '45vh',
    overflowY: 'auto',
    background: 'transparent',
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '8px',
    fontSize: '14px',
    lineHeight: '17px',
    fontWeight: 500,
    fontFamily: 'Inter',

    '&::-webkit-scrollbar': {
      width: '5px',
      borderRadius: '20px',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 20,
      backgroundColor: theme.palette.secondary.dark,
    },
  },
  tableHeaderCell: {
    background: theme.palette.primary.light,

    '& .MuiTableCell-root': {
      textAlign: 'left',
      color: theme.palette.text.secondary,
      borderBottom: '1px solid #515151',
      fontWeight: 500,
      fontFamily: 'Inter',
      fontSize: '14px',
      height: '35px',
      padding: '12px 8px',
      whiteSpace: 'nowrap',
    },
  },
  tableBodyCell: {
    '& .MuiTableCell-root': {
      textAlign: 'left',
      color: theme.palette.common.white,
      borderBottom: '1px solid #4A4E5D',
      fontWeight: 400,
      fontFamily: 'Inter',
      fontSize: '14px',
      height: '35px',
      padding: '12px 8px',
    },
  },
  noResultBox: {
    minHeight: '120px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    padding: '16px',
  },
  noResultTitle: {
    fontSize: '16px',
    fontWeight: 700,
  },
  noResultText: {
    marginTop: '10px',
    maxWidth: 'inherit',
    color: theme.palette.secondary.main,
    fontSize: '13px',
    lineHeight: 1.35,
  },
  infoBox: {
    marginTop: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    backgroundColor: '#303B4F',
    color: theme.palette.secondary.main,
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '13px',
  },
  existingVendorChip: {
    height: '20px',
    borderRadius: '10px',
    border: `1px solid ${VendorStatusMap['EXISTING']?.color}`,
    background: `${VendorStatusMap['EXISTING']?.color}19`,
    '& .MuiChip-label': {
      fontFamily: 'Inter',
      fontSize: '11px',
      fontWeight: '500',
      color: VendorStatusMap['EXISTING']?.color,
      padding: '2px 6px',
    },
  },
}));

interface FindNetSuiteVendorPopupProps {
  open: boolean;
  lienName?: string;
  lienId?: number;
  searchTerm?: string;
  onClose: () => void;
  entityId?: number;
  onVendorSelect?: (vendorName: string, vendorId: number, vendorInternalId?: string) => void;
  onVendorIdSelect?: (
    vendorInternalId: string,
    vendorExistInVlaw?: boolean,
    nativeEntityType?: string,
    nativeName?: string,
    nativeEntityId?: number,
    vendorName?: string,
  ) => void;
  onFindVendorSelect?: (vendorData: NetSuiteVendor) => void;
  synced?: 'isSynced' | 'unsynced' | null;
  entityType?: string;
  entityLabel?: string;
  onVendorSelectFromNetsuite?: (vendorInternalId: string, vendorName: string) => void;
  syncAction?: 'sync' | 'relink';
  vendorCreate?: boolean;
}

interface FindNetSuiteVendorFormValues {
  searchBy: NetSuiteVendorSearchField | QuickBookVendorSearchField;
  searchQuery: string;
}

const DEFAULT_SEARCH_FIELD_NETSUITE = 'name' as NetSuiteVendorSearchField;
const DEFAULT_SEARCH_FIELD_QUICKBOOKS = 'DisplayName' as QuickBookVendorSearchField;
const DEFAULT_SEARCH_FIELD = localStorage.getItem('accountingSystem') === 'NETSUITE'
  ? DEFAULT_SEARCH_FIELD_NETSUITE
  : DEFAULT_SEARCH_FIELD_QUICKBOOKS;

const vendorType = accountingVendorType;

const searchOptions =
  localStorage.getItem('accountingSystem') === 'NETSUITE'
    ? SEARCH_BY_OPTIONS_NETSUITE
    : SEARCH_BY_OPTIONS_QUICKBOOKS;

const mapNativeEntityType = (value: string | null | undefined): string => {
  if (!value) return '-';
  return NATIVE_ENTITY_TYPE_MAP[value] || value;
};

const getProviderData = (vendor: NetSuiteVendor): Record<string, unknown> => {
  if (
    vendor.providerData &&
    typeof vendor.providerData === 'object' &&
    !Array.isArray(vendor.providerData)
  ) {
    return vendor.providerData;
  }

  return {};
};

const getProviderString = (vendor: NetSuiteVendor, key: string): string | undefined => {
  const providerData = getProviderData(vendor);
  const value = providerData[key];

  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return undefined;
};

const mapVendorFromProviderData = (vendor: NetSuiteVendor): NetSuiteVendor => {
  return {
    ...vendor,
  };
};

const getVendorExistsInVlaw = (vendor: NetSuiteVendor, entityType: string): boolean => {
  if (entityType === 'Firm') return vendor.firmExistInVlaw ?? false;
  if (entityType === 'Plaintiff') return vendor.clientExistInVlaw ?? false;
  return vendor.vendorExistInVlaw ?? false;
};

const FindNetSuiteVendorPopup = ({
  open,
  lienName = '',
  lienId,
  searchTerm = '',
  onClose,
  onVendorSelect,
  onVendorIdSelect,
  onFindVendorSelect,
  synced,
  entityType = 'Vendor',
  entityLabel = 'Lien',
  entityId,
  onVendorSelectFromNetsuite,
  syncAction = 'sync',
  vendorCreate,
}: FindNetSuiteVendorPopupProps) => {
  const styles = useStyles();
  const { setAlert } = useSnackBar();
  const navigate = useNavigate();

  const [searchInputValue, setSearchInputValue] = useState(searchTerm);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [vendors, setVendors] = useState<NetSuiteVendor[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<NetSuiteVendor | null>(null);
  const [isVendorDetailsLoading, setIsVendorDetailsLoading] = useState(false);

  const formMethods = useForm<FindNetSuiteVendorFormValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      searchBy: DEFAULT_SEARCH_FIELD,
      searchQuery: searchTerm,
    },
  });

  const { reset, setValue, watch } = formMethods;

  const searchBy = watch('searchBy');

  const normalizeVendors = (data: { items?: NetSuiteVendor[] }): NetSuiteVendor[] => {
    return (data?.items || []).map(mapVendorFromProviderData);
  };

  const loadDefaultVendors = async () => {
    setIsDataLoading(true);
    setVendors([]);

    try {
      const response = await searchNetSuiteVendor({
        field: DEFAULT_SEARCH_FIELD,
        term: '',
        page: 1,
        size: 20,
      });

      setVendors(normalizeVendors(response.data));
    } catch (error) {
      setAlert(() => ({
        message: `Failed to load ${vendorType} vendors.`,
        type: 'error',
      }));
      setVendors([]);
    } finally {
      setIsDataLoading(false);
    }
  };

  const fetchNetSuiteVendors = async (
    field: NetSuiteVendorSearchField | QuickBookVendorSearchField,
    term: string,
  ) => {
    if (!term || term.length <= 2) {
      setVendors([]);
      return;
    }

    setIsDataLoading(true);

    try {
      const response = await searchNetSuiteVendor({
        field,
        term,
        page: 1,
        size: 20,
      });

      setVendors(normalizeVendors(response.data));
    } catch (error) {
      setAlert(() => ({
        message: `Failed to search ${vendorType} vendors.`,
        type: 'error',
      }));
      setVendors([]);
    } finally {
      setIsDataLoading(false);
    }
  };

  const handleSearchVendor = (value: string) => {
    setSearchInputValue(value);
    setValue('searchQuery', value);
  };

  const handleSearchButtonClick = () => {
    const searchValue = searchInputValue.trim();
    if (searchValue.length > 2) {
      fetchNetSuiteVendors(searchBy, searchValue);
    } else if (searchValue.length === 0) {
      loadDefaultVendors();
    }
  };

  const handleClosePopup = () => {
    reset();
    setSearchInputValue('');
    setVendors([]);
    setSelectedVendor(null);
    onClose();
  };


  const isSelectFromNetsuiteFlow = Boolean(onVendorSelectFromNetsuite) && entityLabel !== 'Vendor';

  const handleVendorRowClick = async (vendor: NetSuiteVendor) => {

    if (onVendorIdSelect) {
      if (vendor.vendorExistInVlaw || vendor.firmExistInVlaw || vendor.clientExistInVlaw) {
        if (entityType === mapNativeEntityType(vendor.nativeEntityType)) {
          if (entityId === vendor.nativeEntityId) {
            setAlert(() => ({
              message: `This vendor from ${vendorType} is already synced with the same.`,
              type: 'info',
            }));
            return;
          }
        } else {
          setAlert(() => ({
            message: `This vendor from ${vendorType} is already synced with the VLaw ${mapNativeEntityType(
              vendor.nativeEntityType,
            )} ${vendor.nativeName ?? '-'} and cannot be used here.`,
            type: 'info',
          }));
          return;
        }
      }
      // Selectable vendor: fall through to load details and open the review popup
    }

    if (!onVendorIdSelect && isSelectFromNetsuiteFlow) {
      if (vendor.vendorExistInVlaw || vendor.firmExistInVlaw || vendor.clientExistInVlaw) {
        if (entityId === vendor.nativeEntityId) {
          setAlert(() => ({
            message: `This vendor from ${vendorType} is already synced with the same.`,
            type: 'info',
          }));
        }
        else {
          setAlert(() => ({
            message: `This vendor from ${vendorType} is already synced with the VLaw ${mapNativeEntityType(vendor.nativeEntityType)} ${vendor.nativeName ?? '-'} and cannot be used here.`,
            type: 'info',
          }));
        }
        return;
      }
      // Unsynced vendor: fall through to load details and open the review popup
    }

    if (!onVendorIdSelect && !isSelectFromNetsuiteFlow && onVendorSelect) {
      if (vendor.vendorExistInVlaw || vendor.firmExistInVlaw || vendor.clientExistInVlaw) {
        if (entityType === mapNativeEntityType(vendor.nativeEntityType)) {
          onVendorSelect(vendor.nativeName || '', vendor?.nativeEntityId || 0, vendor.internalId);
          handleClosePopup();
        }
        else {
          setAlert(() => ({
            message: `This vendor from ${vendorType} is already synced with the VLaw ${mapNativeEntityType(vendor.nativeEntityType)} ${vendor.nativeName ?? '-'} and cannot be used here.`,
            type: 'info',
          }));
        }
        return;
      }
      // else {
      //   onVendorSelect(vendor.nativeName || '', vendor?.nativeEntityId || 0);
      //   handleClosePopup();
      // }
      // return;
    }

    setIsVendorDetailsLoading(true);

    try {
      const response = await getNetSuiteVendor(vendor.internalId);

      setSelectedVendor(
        mapVendorFromProviderData({
          ...vendor,
          ...response.data,
        }),
      );
    } catch (error) {
      setAlert(() => ({
        message: `Failed to load ${vendorType} vendor details.`,
        type: 'error',
      }));
    } finally {
      setIsVendorDetailsLoading(false);
    }
  };


  useEffect(() => {
    if (!open) {
      reset();
      setSearchInputValue('');
      setVendors([]);
      setSelectedVendor(null);
      return;
    }

    setSelectedVendor(null);

    reset({
      searchBy: DEFAULT_SEARCH_FIELD,
      searchQuery: searchTerm,
    });

    setSearchInputValue(searchTerm);

    if (searchTerm.length > 2) {
      fetchNetSuiteVendors(DEFAULT_SEARCH_FIELD, searchTerm);
    } else {
      loadDefaultVendors();
    }
  }, [open, searchTerm]);

  return (
    <>
      <Dialog
        open={open && !selectedVendor}
        onClose={handleClosePopup}
        className={styles.classes.popupContainer}
        maxWidth={false}
      >
        <Box className={styles.classes.header}>
          <Box>
            <Box className={styles.classes.titleRow}>
              <BusinessIcon />
              <Typography className={styles.classes.title}>Find {entityType} in {vendorType}</Typography>
            </Box>

          {!onFindVendorSelect && <Typography className={styles.classes.subtitle}>{entityLabel} - {lienName}</Typography>}
          </Box>

          <IconButton onClick={handleClosePopup} className={styles.classes.closeButton}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            padding: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <FormProvider {...formMethods}>
            <Box className={styles.classes.searchRow}>
              <Box>
                <FormSelectField
                  name="searchBy"
                  label="Search by"
                  options={searchOptions}
                  width="100%"
                />
              </Box>

              <Box display="flex" gap="12px" alignItems="flex-end">
                <Box flex="1">
                  <FormTextField
                    name="searchQuery"
                    label="Search"
                    handleChange={handleSearchVendor}
                    inputProps={{
                      onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Enter') handleSearchButtonClick();
                      },
                    }}
                  />
                </Box>
                <CommonButton
                  type="infoMain"
                  buttonText="Search"
                  disabled={
                    searchInputValue.trim().length === 0 ||
                    (searchInputValue.trim().length > 0 && searchInputValue.trim().length < 3)
                  }
                  onButtonClick={handleSearchButtonClick}
                  minWidth="100px"
                />
              </Box>
            </Box>
          </FormProvider>
          {isVendorDetailsLoading ? (
            <Box py={6} display="flex" justifyContent="center">
              <Loader size={40} colorType="info" />
            </Box>
          ) : (
            <>
              <TableContainer
                className={styles.classes.tableContainer}
                component={Paper}
                elevation={0}
              >
                <Table sx={{ width: '100%', tableLayout: 'fixed' }} size="small">
                  <TableHead className={styles.classes.tableHeaderCell}>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>VLaw Entitiy Name</TableCell>
                      <TableCell>Vlaw Entity Type</TableCell>
                      <TableCell>Entity ID</TableCell>
                      <TableCell>Tax ID</TableCell>

                    </TableRow>
                  </TableHead>

                  <TableBody className={clsx(styles.classes.tableBodyCell)}>
                    {onFindVendorSelect ? (
                      <>
                        {isDataLoading ? (
                          <TableRow>
                            <TableCell
                              colSpan={5}
                              align="center"
                              sx={{ textAlign: 'center !important' }}
                            >
                              <Box py={4}>
                                <Loader size={40} colorType="info" />
                              </Box>
                            </TableCell>
                          </TableRow>
                        ) : vendors.length > 0 ? (
                          vendors.map((vendor) => {
                            const isVendorExists = vendor.vendorExistInVlaw || vendor.firmExistInVlaw || vendor.clientExistInVlaw;
                            return (
                              <TableRow
                                key={vendor.internalId}
                                onClick={() => !isVendorExists && handleVendorRowClick(vendor)}
                                sx={{ cursor: isVendorExists ? 'not-allowed' : 'pointer', opacity: isVendorExists ? 0.5 : 1 }}
                              >
                                <TableCell
                                  sx={{
                                    overflow: 'visible !important',
                                    whiteSpace: 'normal !important',
                                  }}
                                >
                                  <Box display="flex" alignItems="center" gap='3px' flexWrap="wrap">
                                    {/* <span>{vendor.name || '-'}</span> */}
                                    <Box minWidth={0} flex="0 1 auto">
                                      <TruncatedText text={vendor.name || '-'} maxLength={15} />
                                    </Box>

                                    {/* {isVendorExists && (<Box
                                      sx={{
                                        backgroundColor: 'success.main',
                                        color: 'white',
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        padding: '3px 8px',
                                        borderRadius: '8px',
                                        whiteSpace: 'nowrap',
                                        flexShrink: 0,
                                      }}
                                    >
                                      Exist in VLaw
                                    </Box>
                                    )} */}
                                 {isVendorExists &&   <Chip
                                      label={VendorStatusMap['EXIST_IN_VLAW']?.label}
                                      variant="outlined"
                                      className={styles.classes.existingVendorChip}
                                    />}
                                  </Box>
                                </TableCell>

                                <TableCell>
                                  <Box minWidth={0} flex="0 1 auto">
                                    <TruncatedText text={vendor.nativeName || '-'} maxLength={15} />
                                  </Box>

                                </TableCell>

                                <TableCell>
                                  <Box minWidth={0} flex="0 1 auto">
                                    <TruncatedText text={mapNativeEntityType(vendor.nativeEntityType)} maxLength={15} />
                                  </Box>
                                </TableCell>

                                <TableCell>
                                  <Box minWidth={0} flex="0 1 auto">
                                    <TruncatedText
                                      text={getProviderString(vendor, 'entityId') || '-'}
                                      maxLength={15}
                                    />
                                  </Box>
                                </TableCell>

                                <TableCell>
                                  {getProviderString(vendor, 'taxIdLast4') || '-'}
                                </TableCell>
                              </TableRow>
                            );
                          })
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={5}
                              align="center"
                              sx={{ textAlign: 'center !important' }}
                            >
                              <Box className={styles.classes.noResultBox}>
                                <Typography className={styles.classes.noResultTitle}>
                                  {searchInputValue.length > 0 && searchInputValue.length <= 2
                                    ? 'Type at least 3 symbols to search.'
                                    : `No matching ${vendorType} vendors`}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    ) : (
                      <>
                        {isDataLoading ? (
                          <TableRow>
                            <TableCell
                              colSpan={5}
                              align="center"
                              sx={{ textAlign: 'center !important' }}
                            >
                              <Box py={4}>
                                <Loader size={40} colorType="info" />
                              </Box>
                            </TableCell>
                          </TableRow>
                        ) : vendors.length > 0 ? (
                          vendors.map((vendor) => (
                            <TableRow
                              key={vendor.internalId}
                              onClick={() => handleVendorRowClick(vendor)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell
                                sx={{
                                  overflow: 'visible !important',
                                  whiteSpace: 'normal !important',
                                }}
                              >
                                <Box display="flex" alignItems="center" gap='3px' flexWrap="wrap">
                                  {/* <span>{vendor.name || '-'}</span> */}
                                  <Box minWidth={0} flex="0 1 auto">
                                    <TruncatedText text={vendor.name || '-'} maxLength={15} />
                                  </Box>

                                  {(vendor.vendorExistInVlaw || vendor.firmExistInVlaw || vendor.clientExistInVlaw) && (<Box
                                    sx={{
                                      backgroundColor: 'success.main',
                                      color: 'white',
                                      fontSize: '11px',
                                      fontWeight: 600,
                                      padding: '3px 8px',
                                      borderRadius: '8px',
                                      whiteSpace: 'nowrap',
                                      flexShrink: 0,
                                    }}
                                  >
                                    Synced
                                  </Box>
                                  )}
                                </Box>
                              </TableCell>

                              <TableCell>
                                <Box minWidth={0} flex="0 1 auto">
                                  <TruncatedText text={vendor.nativeName || '-'} maxLength={15} />
                                </Box>

                              </TableCell>

                              <TableCell>
                                <Box minWidth={0} flex="0 1 auto">
                                  <TruncatedText text={mapNativeEntityType(vendor.nativeEntityType)} maxLength={15} />
                                </Box>
                              </TableCell>

                              <TableCell>
                                <Box minWidth={0} flex="0 1 auto">
                                  <TruncatedText
                                    text={getProviderString(vendor, 'entityId') || '-'}
                                    maxLength={15}
                                  />
                                </Box>
                              </TableCell>

                              <TableCell>
                                {getProviderString(vendor, 'taxIdLast4') || '-'}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={5}
                              align="center"
                              sx={{ textAlign: 'center !important' }}
                            >
                              <Box className={styles.classes.noResultBox}>
                                <Typography className={styles.classes.noResultTitle}>
                                  {searchInputValue.length > 0 && searchInputValue.length <= 2
                                    ? 'Type at least 3 symbols to search.'
                                    : `No matching ${vendorType} vendors`}
                                </Typography>

                                {/* {searchInputValue.length > 2 && (
                                  <Typography className={styles.classes.noResultText}>
                                    Vendor creation from V.Law is not supported in this phase. Please
                                    have the vendor created directly in NetSuite, then re-run the
                                    search.
                                  </Typography>
                                )} */}
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
            <Box className={styles.classes.infoBox}>
              <InfoOutlinedIcon />
              <Typography sx={{ fontSize: '12px' }}>
                {vendorType} supports exact and wildcard search only — try a different field or
                shorter term if results look off.
              </Typography>
            </Box>
            {vendorType === 'QuickBooks' && !vendorCreate && (
              <CommonButton
                type="infoMain"
                buttonText={`Create Vendor in ${vendorType}`}
                // disabled={isSelectingNetSuiteVendor}
                onButtonClick={() =>
                  navigate('/vendors/create-quickbooks-vendor-account', {
                    state: { entityType: entityType, entityId: entityId },
                  })
                }
              />
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {onVendorSelect &&
        <NetSuiteVendorReviewPopup
          open={Boolean(selectedVendor)}
          lienName={lienName}
          lienId={lienId}
          vendor={selectedVendor}
          onBack={() => setSelectedVendor(null)}
          onClose={handleClosePopup}
          onVendorSelect={onVendorSelect}
          synced={synced}
        />}

      {onVendorIdSelect &&
        <NetSuiteVendorReviewPopup
          open={Boolean(selectedVendor)}
          lienName={lienName}
          lienId={lienId}
          vendor={selectedVendor}
          onBack={() => setSelectedVendor(null)}
          onClose={handleClosePopup}
          syncAction={syncAction}
          warningMessage={
            selectedVendor && getVendorExistsInVlaw(selectedVendor, entityType)
              ? `This vendor from NetSuite is already synced with the VLaw ${mapNativeEntityType(selectedVendor.nativeEntityType)} ‘${selectedVendor.nativeName}’.`
              : undefined
          }
          onSyncOrRelink={() => {
            if (!selectedVendor) return;
            onVendorIdSelect(
              selectedVendor.internalId,
              getVendorExistsInVlaw(selectedVendor, entityType),
              mapNativeEntityType(selectedVendor.nativeEntityType),
              selectedVendor.nativeName ?? undefined,
              selectedVendor.nativeEntityId ?? undefined,
              selectedVendor.name,
            );
          }}
        />}

      {!onVendorIdSelect && isSelectFromNetsuiteFlow &&
        <NetSuiteVendorReviewPopup
          open={Boolean(selectedVendor)}
          lienName={lienName}
          lienId={lienId}
          vendor={selectedVendor}
          onBack={() => setSelectedVendor(null)}
          onClose={handleClosePopup}
          syncAction={syncAction}
          onSyncOrRelink={() => {
            if (!selectedVendor) return;
            onVendorSelectFromNetsuite?.(selectedVendor.internalId, selectedVendor.name);
          }}
        />}

      {onFindVendorSelect &&
        <NetSuiteVendorReviewPopup
          open={Boolean(selectedVendor)}
          lienName={lienName}
          lienId={lienId}
          vendor={selectedVendor}
          onBack={() => setSelectedVendor(null)}
          onClose={handleClosePopup}
          onFindVendorSelect={onFindVendorSelect}
          synced={synced}
        />}
    </>
  );
};

export default FindNetSuiteVendorPopup;